/*----------------------------------
- COMPONENT: GLOSSARY TERM
----------------------------------*/

/**
 * @docs docs/features/glossary
 *
 * One word of trade vocabulary with its beginner definition attached.
 *
 * Deliberately not a JavaScript popover, for the same reason as the public table's hover card:
 * this definition is the page's GEO content, and a card that only exists after hydration is a card
 * no crawler and no answer engine ever reads. The definition ships in the server HTML, and CSS
 * `:hover` / `:focus-within` do the disclosure. `tabindex` is what makes the card reachable by
 * keyboard and by tap, where `:hover` never fires.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

import { glossaryTermHref, type GlossaryTerm as GlossaryTermEntry } from '@uniquedomains/components/glossary/terms';

/*----------------------------------
- TYPES
----------------------------------*/

type GlossaryTermProps = {
    term: GlossaryTermEntry;
    /** The surface form as it appears in the sentence, so the copy is never rewritten. */
    children: string;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const GlossaryTerm = ({ term, children }: GlossaryTermProps) => {
    // Not derived from the term: the same word is explained once per section, so a page carries
    // several cards for it, and a repeated id would point every `aria-describedby` at the first.
    const cardId = React.useId();

    return (
        <dfn class="ud-term" tabIndex={0} aria-describedby={cardId}>
            {children}
            <span id={cardId} role="note" class="ud-term__card">
                <span class="ud-term__card-title">{term.term}</span>
                <span class="ud-term__card-body">{term.definition}</span>
                <a class="ud-term__card-link" href={glossaryTermHref(term)}>
                    Learn more
                </a>
            </span>
        </dfn>
    );
};

export default GlossaryTerm;
